import { Observable, Subject, takeUntil, tap, throttleTime } from 'rxjs';

export interface SimpleClickProtectionConfig {
  /** Tiempo mínimo entre clicks válidos en milisegundos */
  safeTime: number;
  /** Habilitar logging para debug */
  enableLogging: boolean;
  /** Nombre del contexto para logging */
  context: string;
}

type LogLevel = 'log' | 'warn' | 'error';

/**
 * Clase optimizada para protección contra doble clicks en Angular
 * Incluye todas las optimizaciones:
 * 1. Propiedades privadas con # (ES2020+)
 * 2. validClicks$ creado una sola vez en constructor
 * 3. Performance mejorada
 */
export class ClickProtection<T = string> {
  readonly #config: SimpleClickProtectionConfig;
  readonly #clicked$ = new Subject<T>();
  readonly #destroy$ = new Subject<void>();
  readonly #validClicksObservable: Observable<T>;
  #lastAllowedClick = 0;
  #isDestroyed = false;

  constructor({
    safeTime = 250,
    enableLogging = false,
    context = 'ClickProtection',
  }: Partial<SimpleClickProtectionConfig> = {}) {
    this.#config = {
      safeTime: safeTime,
      enableLogging: enableLogging,
      context: context,
    };

    // Crear el observable de clicks válidos una sola vez para mejor performance
    // Esta es la optimización clave: el pipe se ejecuta solo una vez
    this.#validClicksObservable = this.#clicked$.pipe(
      tap((value) => {
        const now = performance.now();
        const diff = now - this.#lastAllowedClick;
        if (diff < this.#config.safeTime) {
          const diffFmt = diff.toFixed(3);
          const message = `Click ignorado para "${value}" después de ${diffFmt}ms`;
          this._log(message, 'warn');
        }
      }),
      throttleTime(this.#config.safeTime, undefined, {
        leading: true,
        trailing: false,
      }),
      tap(() => {
        this.#lastAllowedClick = performance.now();
      }),
      takeUntil(this.#destroy$)
    );

    this._log('Inicializando protección de clicks');
  }

  /**
   * Observable que emite solo los clicks válidos (filtrados por throttle)
   * OPTIMIZACIÓN: Retorna la misma instancia creada en el constructor
   */
  get validClicks$(): Observable<T> {
    return this.#validClicksObservable;
  }

  /**
   * Registra un click y lo procesa a través del filtro de protección
   * @param value Valor a emitir si el click es válido
   */
  click(value: T): void {
    if (this.#isDestroyed) {
      this._log('Intento de click en instancia destruida', 'warn');
      return;
    }

    this.#clicked$.next(value);
  }

  /**
   * Suscribe un callback que se ejecutará solo para clicks válidos
   * @param callback Función a ejecutar para clicks válidos
   * @returns Función para cancelar la suscripción
   */
  onValidClick(callback: (value: T) => void): () => void {
    if (this.#isDestroyed) {
      this._log('Intento de suscripción en instancia destruida', 'warn');
      return () => {
        /* no-op */
      };
    }

    const subscription = this.#validClicksObservable.subscribe(callback);
    const rv = () => subscription.unsubscribe();
    return rv;
  }

  /**
   * Verifica si un click sería válido en este momento (sin registrarlo)
   */
  isClickAllowed(): boolean {
    const now = performance.now();
    const diff = now - this.#lastAllowedClick;
    const rv = diff >= this.#config.safeTime;
    return rv;
  }

  /**
   * Obtiene el tiempo restante hasta que se permita el próximo click
   */
  getTimeUntilNextClick(): number {
    const now = performance.now();
    const diff = now - this.#lastAllowedClick;
    const rv = Math.max(0, this.#config.safeTime - diff);
    return rv;
  }

  /**
   * Limpia recursos y cancela todas las suscripciones
   */
  destroy(): void {
    if (this.#isDestroyed) {
      return;
    }

    this._log('Destruyendo instancia');
    this.#isDestroyed = true;
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  private _log(message: string, level: LogLevel = 'log'): void {
    if (this.#config.enableLogging) {
      const fullMessage = `${this.#config.context}: ${message}`;
      console[level](fullMessage);
    }
  }
}
